import React, { useState, useEffect } from 'react';
import { Repeat, Plus, X, Search, Users, Calendar, MapPin, Phone, Trash2, Clock, User } from 'lucide-react';
import { dbProxy } from '../lib/db-proxy';

interface ScambiEvent {
  id: string;
  name: string;
  date: string;
  time: string;
  location: string;
  notes: string;
  tables: ScambiTable[];
}

interface ScambiTable {
  id: string;
  name: string;
  guests: ScambiGuest[];
}

interface ScambiGuest {
  contactId: string;
  name: string;
  phone: string;
  gender: string;
  age: number | null;
}

interface Contact {
  id: string;
  name: string;
  phone: string;
  gender: string;
  birthdate: string | null;
  city: string;
  contact_status: string;
}

const calculateAge = (birthdate: string | null): number | null => {
  if (!birthdate) return null;
  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const SerateScambi = () => {
  const [events, setEvents] = useState<ScambiEvent[]>([]);
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ScambiEvent | null>(null);
  const [showAddGuest, setShowAddGuest] = useState<string | null>(null);
  const [guestSearch, setGuestSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const [newEvent, setNewEvent] = useState({
    name: '',
    date: '',
    time: '21:00',
    location: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [eventsRes, contactsRes] = await Promise.all([
        dbProxy.from('scambi_events').select('*').order('date', { ascending: false }),
        dbProxy.from('contacts').select('id, name, phone, gender, birthdate, city, contact_status'),
      ]);

      if (contactsRes.data) {
        setAllContacts(contactsRes.data);
      }

      if (eventsRes.data) {
        const loadedEvents: ScambiEvent[] = eventsRes.data.map((e: any) => ({
          id: e.id,
          name: e.name,
          date: e.date,
          time: e.time || '21:00',
          location: e.location || '',
          notes: e.notes || '',
          tables: e.tables_data || [],
        }));
        setEvents(loadedEvents);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.name || !newEvent.date) return;
    setSaving(true);
    try {
      const { data, error } = await dbProxy.from('scambi_events').insert({
        name: newEvent.name,
        date: newEvent.date,
        time: newEvent.time,
        location: newEvent.location,
        notes: newEvent.notes,
        tables_data: [{ id: crypto.randomUUID(), name: 'Tavolo 1', guests: [] }],
      }).select('*').maybeSingle();

      if (!error && data) {
        const created: ScambiEvent = {
          id: data.id,
          name: data.name,
          date: data.date,
          time: data.time,
          location: data.location || '',
          notes: data.notes || '',
          tables: data.tables_data || [],
        };
        setEvents(prev => [created, ...prev]);
        setSelectedEvent(created);
        setShowNewEvent(false);
        setNewEvent({ name: '', date: '', time: '21:00', location: '', notes: '' });
      }
    } catch (err) {
      console.error('Error creating event:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Eliminare questa serata scambi?')) return;
    try {
      await dbProxy.from('scambi_events').delete().eq('id', eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      if (selectedEvent?.id === eventId) setSelectedEvent(null);
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const saveTablesData = async (event: ScambiEvent) => {
    try {
      await dbProxy.from('scambi_events').update({ tables_data: event.tables }).eq('id', event.id);
    } catch (err) {
      console.error('Error saving tables:', err);
    }
  };

  const addTable = () => {
    if (!selectedEvent) return;
    const tableNum = selectedEvent.tables.length + 1;
    const updated = {
      ...selectedEvent,
      tables: [...selectedEvent.tables, { id: crypto.randomUUID(), name: `Tavolo ${tableNum}`, guests: [] }],
    };
    setSelectedEvent(updated);
    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
    saveTablesData(updated);
  };

  const removeTable = (tableId: string) => {
    if (!selectedEvent) return;
    const updated = {
      ...selectedEvent,
      tables: selectedEvent.tables.filter(t => t.id !== tableId),
    };
    setSelectedEvent(updated);
    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
    saveTablesData(updated);
  };

  const addGuestToTable = (tableId: string, contact: Contact) => {
    if (!selectedEvent) return;
    const alreadyAdded = selectedEvent.tables.some(t => t.guests.some(g => g.contactId === contact.id));
    if (alreadyAdded) return;

    const guest: ScambiGuest = {
      contactId: contact.id,
      name: contact.name,
      phone: contact.phone,
      gender: contact.gender,
      age: calculateAge(contact.birthdate),
    };

    const updated = {
      ...selectedEvent,
      tables: selectedEvent.tables.map(t =>
        t.id === tableId ? { ...t, guests: [...t.guests, guest] } : t
      ),
    };
    setSelectedEvent(updated);
    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
    saveTablesData(updated);
    setShowAddGuest(null);
    setGuestSearch('');
  };

  const removeGuestFromTable = (tableId: string, contactId: string) => {
    if (!selectedEvent) return;
    const updated = {
      ...selectedEvent,
      tables: selectedEvent.tables.map(t =>
        t.id === tableId ? { ...t, guests: t.guests.filter(g => g.contactId !== contactId) } : t
      ),
    };
    setSelectedEvent(updated);
    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
    saveTablesData(updated);
  };

  const renameTable = (tableId: string, newName: string) => {
    if (!selectedEvent) return;
    const updated = {
      ...selectedEvent,
      tables: selectedEvent.tables.map(t =>
        t.id === tableId ? { ...t, name: newName } : t
      ),
    };
    setSelectedEvent(updated);
    setEvents(prev => prev.map(e => e.id === updated.id ? updated : e));
    saveTablesData(updated);
  };

  const filteredGuestsForAdd = allContacts.filter(c => {
    if (!guestSearch) return false;
    const q = guestSearch.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  const formatDate = (d: string) => new Date(d).toLocaleDateString('it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  const totalGuests = selectedEvent?.tables.reduce((sum, t) => sum + t.guests.length, 0) || 0;

  return (
    <div className="p-4 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Repeat className="h-6 w-6 text-amber-500" />
            Serate Scambi
          </h1>
          <p className="text-gray-500 mt-1">Organizza le serate scambi e componi i tavoli con i partecipanti</p>
        </div>
        <button
          onClick={() => setShowNewEvent(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D91C5C] text-white rounded-xl text-sm font-medium hover:bg-[#C01850] transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" />
          Nuova Serata
        </button>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Event List */}
        <div className="space-y-3">
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Serate</h2>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-6 w-6 border-2 border-[#D91C5C]/20 border-t-[#D91C5C] rounded-full animate-spin" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessuna serata ancora</p>
              </div>
            ) : (
              <div className="space-y-2">
                {events.map(event => {
                  const guestCount = event.tables.reduce((s, t) => s + t.guests.length, 0);
                  return (
                    <button
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                        selectedEvent?.id === event.id
                          ? 'bg-amber-50 border border-amber-200'
                          : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <div className="font-medium text-gray-900 text-sm">{event.name}</div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(event.date).toLocaleDateString('it-IT')}</span>
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{guestCount}</span>
                        <span className="flex items-center gap-1">{event.tables.length} tavoli</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Event Detail / Table Composer */}
        <div>
          {!selectedEvent ? (
            <div className="bg-white rounded-2xl border border-gray-200 flex items-center justify-center py-20">
              <div className="text-center text-gray-400">
                <Repeat className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Seleziona una serata</p>
                <p className="text-sm mt-1">oppure creane una nuova</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Event header */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedEvent.name}</h2>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{formatDate(selectedEvent.date)}</span>
                      <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{selectedEvent.time}</span>
                      {selectedEvent.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{selectedEvent.location}</span>}
                      <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />{totalGuests} partecipanti</span>
                    </div>
                    {selectedEvent.notes && <p className="text-sm text-gray-500 mt-2">{selectedEvent.notes}</p>}
                  </div>
                  <button
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Elimina serata"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Tables */}
              <div className="grid md:grid-cols-2 gap-4">
                {selectedEvent.tables.map(table => (
                  <div key={table.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                      <input
                        type="text"
                        value={table.name}
                        onChange={e => renameTable(table.id, e.target.value)}
                        className="font-semibold text-gray-900 bg-transparent border-none p-0 focus:ring-0 text-sm w-32"
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400 mr-2">{table.guests.length} partecipanti</span>
                        <button
                          onClick={() => { setShowAddGuest(table.id); setGuestSearch(''); }}
                          className="p-1.5 text-[#D91C5C] hover:bg-[#D91C5C]/10 rounded-lg transition-colors"
                          title="Aggiungi partecipante"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        {table.guests.length === 0 && selectedEvent.tables.length > 1 && (
                          <button
                            onClick={() => removeTable(table.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Rimuovi tavolo"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="p-3">
                      {table.guests.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-4">Nessun partecipante</p>
                      ) : (
                        <div className="space-y-1.5">
                          {table.guests.map(guest => (
                            <div key={guest.contactId} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group">
                              <Repeat className="h-3 w-3 text-amber-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{guest.name}</div>
                                <div className="text-xs text-gray-400">
                                  {guest.gender === 'M' ? 'Uomo' : guest.gender === 'F' ? 'Donna' : guest.gender}
                                  {guest.age ? `, ${guest.age} anni` : ''}
                                </div>
                              </div>
                              <button
                                onClick={() => removeGuestFromTable(table.id, guest.contactId)}
                                className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                title="Rimuovi"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add table button */}
                <button
                  onClick={addTable}
                  className="border-2 border-dashed border-gray-200 rounded-2xl py-8 flex flex-col items-center justify-center text-gray-400 hover:border-amber-300 hover:text-amber-500 transition-colors"
                >
                  <Plus className="h-6 w-6 mb-1" />
                  <span className="text-sm font-medium">Aggiungi Tavolo</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Event Modal */}
      {showNewEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNewEvent(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Nuova Serata Scambi</h2>
              <button onClick={() => setShowNewEvent(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Serata *</label>
                <input
                  type="text"
                  value={newEvent.name}
                  onChange={e => setNewEvent(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-xl border-gray-300 focus:ring-amber-200 focus:border-amber-400 text-sm"
                  placeholder="es. Serata Scambi Settembre"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={e => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full rounded-xl border-gray-300 focus:ring-amber-200 focus:border-amber-400 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ora</label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={e => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full rounded-xl border-gray-300 focus:ring-amber-200 focus:border-amber-400 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={e => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full rounded-xl border-gray-300 focus:ring-amber-200 focus:border-amber-400 text-sm"
                  placeholder="es. Ristorante Da Mario"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={newEvent.notes}
                  onChange={e => setNewEvent(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full rounded-xl border-gray-300 focus:ring-amber-200 focus:border-amber-400 text-sm"
                  rows={2}
                  placeholder="Note aggiuntive..."
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setShowNewEvent(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Annulla</button>
              <button
                onClick={handleCreateEvent}
                disabled={!newEvent.name || !newEvent.date || saving}
                className="px-5 py-2 bg-[#D91C5C] text-white rounded-xl text-sm font-medium hover:bg-[#C01850] disabled:opacity-50 transition-colors"
              >
                {saving ? 'Salvataggio...' : 'Crea Serata'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Guest Modal */}
      {showAddGuest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowAddGuest(null); setGuestSearch(''); }}>
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Aggiungi Partecipante</h2>
              <button onClick={() => { setShowAddGuest(null); setGuestSearch(''); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <div className="px-6 py-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={guestSearch}
                  onChange={e => setGuestSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-amber-200 focus:border-amber-400"
                  placeholder="Cerca cliente per nome o telefono..."
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Cerca un cliente per nome o numero di telefono
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {!guestSearch ? (
                <p className="text-center text-sm text-gray-400 py-8">Digita per cercare un cliente</p>
              ) : filteredGuestsForAdd.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8">Nessun cliente trovato</p>
              ) : (
                <div className="space-y-1">
                  {filteredGuestsForAdd.slice(0, 30).map(contact => {
                    const alreadyAdded = selectedEvent?.tables.some(t => t.guests.some(g => g.contactId === contact.id));
                    return (
                      <button
                        key={contact.id}
                        onClick={() => !alreadyAdded && addGuestToTable(showAddGuest, contact)}
                        disabled={!!alreadyAdded}
                        className={`w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 transition-colors ${
                          alreadyAdded ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'
                        }`}
                      >
                        <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{contact.name}</div>
                          <div className="text-xs text-gray-400">
                            {contact.gender === 'M' ? 'Uomo' : contact.gender === 'F' ? 'Donna' : contact.gender}
                            {contact.birthdate ? `, ${calculateAge(contact.birthdate)} anni` : ''}
                            {contact.city ? ` - ${contact.city}` : ''}
                          </div>
                        </div>
                        {alreadyAdded && <span className="text-xs text-gray-400">Gia' aggiunto</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SerateScambi;
